import { useState } from "react";
import {
  CalendarDays,
  Gamepad2,
  Handshake,
  HelpCircle,
} from "lucide-react";
import { lucideIconRegistry } from "yeati-ui";
import type { LucideIcon } from "lucide-react";

// Icônes référencées par le backend (menu /theme) mais pas encore présentes
// dans le registre partagé yeati-ui. À terme, les ajouter à lucideIconRegistry.
const LOCAL_ICON_SUPPLEMENT: Record<string, LucideIcon> = { CalendarDays, Gamepad2, Handshake };

export type MenuIconComponentProps = {
  size?: number
  className?: string
};

export type MenuIconComponent = React.ComponentType<MenuIconComponentProps>;

const normalizeIconName = (name: string): string =>
  name.toLowerCase().replace(/[-_\s]/g, "");

const ICON_MAP: Record<string, LucideIcon> = {
  // yeati-ui types its registry against its own @types/react copy, which TypeScript treats as unrelated to the app's.
  ...(lucideIconRegistry as Record<string, LucideIcon>),
  ...LOCAL_ICON_SUPPLEMENT,
};

const NORMALIZED_ICON_MAP: Record<string, LucideIcon> = Object.fromEntries(
  Object.entries(ICON_MAP).map(([key, icon]) => [normalizeIconName(key), icon]),
);

const lookupLucideIcon = (iconName: string): LucideIcon | undefined =>
  ICON_MAP[iconName] ?? NORMALIZED_ICON_MAP[normalizeIconName(iconName)];

const DEFAULT_3D_VARIANT = "color";

const create3dIcon = (name: string, variant?: string): MenuIconComponent => {
  const path = `/icons/3d/${variant || DEFAULT_3D_VARIANT}/${name}.webp`;
  const Icon3d = ({ size, className }: MenuIconComponentProps) => (
    <img
      src={path}
      alt=""
      width={size}
      height={size}
      className={className}
      style={{ objectFit: "contain" }}
      loading="lazy"
      aria-hidden="true"
    />
  );
  Icon3d.displayName = `Icon3d_${name}`;
  return Icon3d;
};

const createEmojiIcon = (emoji: string): MenuIconComponent => {
  const EmojiIcon = ({ size, className }: MenuIconComponentProps) => (
    <span
      className={className}
      style={size
        ? {
          fontSize: `${size}px`, width: `${size}px`, height: `${size}px`, display: "inline-flex", alignItems: "center", justifyContent: "center",
        }
        : undefined}
      role="img"
      aria-hidden="true"
    >
      {emoji}
    </span>
  );
  EmojiIcon.displayName = "EmojiIcon";
  return EmojiIcon;
};

const createImageIcon = (url: string, fallbackIconName: string): MenuIconComponent => {
  const ImageIcon = ({ size, className }: MenuIconComponentProps) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
      const FallbackIcon = lookupLucideIcon(fallbackIconName) ?? HelpCircle;
      return <FallbackIcon size={size} className={className} />;
    }

    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        className={className}
        style={{ objectFit: "contain", borderRadius: "4px" }}
        loading="lazy"
        aria-hidden="true"
        onError={() => setHasError(true)}
      />
    );
  };
  ImageIcon.displayName = "ImageIcon";
  return ImageIcon;
};

export const resolveIcon = (
  iconName: string,
  iconType?: string,
  iconVariant?: string,
  iconImage?: string,
): MenuIconComponent => {
  switch (iconType) {
    case "3d":
      return create3dIcon(iconName, iconVariant);
    case "emoji":
      return createEmojiIcon(iconName);
    case "image":
      if (iconImage) {
        return createImageIcon(iconImage, iconName);
      }
      return resolveLucideIconWithWarning(iconName, iconType);
    default:
      return resolveLucideIconWithWarning(iconName, iconType);
  }
};

const resolveLucideIconWithWarning = (
  iconName: string,
  iconType?: string,
): LucideIcon => {
  const icon = lookupLucideIcon(iconName);
  if (!icon && import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.warn("[iconResolver] Unknown icon name:", iconName, { iconType });
  }
  return icon ?? HelpCircle;
};
